// Decision Table evaluation with full hit-policy semantics (DMN 1.3).
//
//  1. Evaluate each `input.inputExpression.text` in the current FEEL env
//     → inputValues[i].
//  2. For each rule, compile each `inputEntry.text` as a FEEL unary test
//     against inputValues[i]. A rule matches when ALL inputs match.
//  3. Apply hit policy to the matching rules' outputs. Output rows are
//     evaluated as FEEL literal expressions in the current env.
//
// Output shape:
//   - single `output` column      → the bare value (or list, for multi-hit)
//   - multiple `output` columns   → object keyed by output.name
//
// Aggregations (SUM/COUNT/MIN/MAX) flatten multi-output rules by applying
// the op per column, matching the camunda/dmn-js convention.

import { evaluate as feelEvaluate } from '../feel/Evaluator';
import { compileUnaryTests } from '../feel/UnaryTest';
import { getHitPolicy } from '../table/hitPolicies';

export class DecisionTableEvalError extends Error {
  constructor(msg, context = null) {
    super(`[simulator/dt] ${msg}`);
    this.context = context;
  }
}

function withContext(fn, context) {
  try { return fn(); }
  catch (err) {
    const msg = err && err.message ? err.message : String(err);
    const wrapped = new DecisionTableEvalError(msg, context);
    wrapped.cause = err;
    throw wrapped;
  }
}

export function evalDecisionTable(table, env) {
  const rawInputs = table.input || [];
  const rawOutputs = table.output || [];
  const rawRules = table.rule || [];
  const policy = getHitPolicy(table.hitPolicy);
  const aggregation = table.aggregation || null;
  const tableId = table.id || null;

  if (!rawInputs.length && !rawOutputs.length) {
    throw new DecisionTableEvalError('table has neither inputs nor outputs', { tableId });
  }

  const inputValues = rawInputs.map((inp, i) => {
    const text = inp.inputExpression && inp.inputExpression.text;
    if (text == null || text === '') return null;
    return withContext(
      () => feelEvaluate(text, env),
      { tableId, inputIdx: i, inputId: inp.id, kind: 'inputExpression', expr: text }
    );
  });

  // Output priorities: per-output, rank outputValues in the declared order
  // so PRIORITY and OUTPUT ORDER can sort matched rules.
  const outputPriority = rawOutputs.map((out) => {
    const values = out.outputValues && out.outputValues.text;
    if (!values) return null;
    try {
      const list = feelEvaluate(values, env);
      if (Array.isArray(list)) return list;
    } catch (_) { /* fall through */ }
    return null;
  });

  const matches = [];
  rawRules.forEach((rule, ruleIdx) => {
    const entries = rule.inputEntry || [];
    for (let i = 0; i < rawInputs.length; i++) {
      const entry = entries[i];
      const expr = (entry && entry.text) != null ? entry.text : '-';
      const ctx = {
        tableId, ruleIdx, ruleId: rule.id, inputIdx: i,
        inputId: (rawInputs[i] && rawInputs[i].id) || null,
        kind: 'inputEntry', expr,
      };
      const testFn = withContext(() => compileUnaryTests(expr), ctx);
      if (!withContext(() => testFn(inputValues[i], env.__feelContext || {}), ctx)) return;
    }
    const values = (rule.outputEntry || []).map((oe, i) => {
      const text = (oe && oe.text) || '';
      if (text === '') return null;
      return withContext(
        () => feelEvaluate(text, env),
        {
          tableId, ruleIdx, ruleId: rule.id, outputIdx: i,
          outputId: (rawOutputs[i] && rawOutputs[i].id) || null,
          kind: 'outputEntry', expr: text,
        }
      );
    });
    matches.push({ ruleIdx, values, annotation: rule.annotationEntry || rule.description });
  });

  const shaped = matches.map((m) => shapeOutputs(m.values, rawOutputs));

  const result = applyPolicy(policy, aggregation, matches, shaped, outputPriority);

  return {
    hit: policy.id,
    aggregation,
    matchedRules: matches.map((m) => rawRules[m.ruleIdx].id),
    inputValues,
    result,
  };
}

function shapeOutputs(values, outputs) {
  if (outputs.length === 1) return values[0];
  const obj = {};
  outputs.forEach((out, i) => {
    obj[out.name || `output_${i + 1}`] = values[i];
  });
  return obj;
}

function applyPolicy(policy, aggregation, matches, shaped, outputPriority) {
  if (!matches.length) return null;

  switch (policy.id) {
    case 'UNIQUE':
      if (matches.length > 1) {
        throw new DecisionTableEvalError(
          `UNIQUE expected a single match, got ${matches.length} (rules ${matches.map((m) => m.ruleIdx + 1).join(',')})`,
          { kind: 'hitPolicy', policy: 'UNIQUE', conflictRuleIdxs: matches.map((m) => m.ruleIdx) }
        );
      }
      return shaped[0];

    case 'ANY': {
      const first = JSON.stringify(shaped[0]);
      for (let i = 1; i < shaped.length; i++) {
        if (JSON.stringify(shaped[i]) !== first) {
          throw new DecisionTableEvalError(
            'ANY: multiple rules matched with different outputs',
            { kind: 'hitPolicy', policy: 'ANY', conflictRuleIdxs: matches.map((m) => m.ruleIdx) }
          );
        }
      }
      return shaped[0];
    }

    case 'FIRST':
      return shaped[0];

    case 'PRIORITY': {
      const sorted = sortByPriority(matches, shaped, outputPriority);
      return sorted[0];
    }

    case 'RULE ORDER':
      return shaped;

    case 'OUTPUT ORDER':
      return sortByPriority(matches, shaped, outputPriority);

    case 'COLLECT':
      return collect(shaped, aggregation, matches);

    default:
      return shaped[0];
  }
}

function sortByPriority(matches, shaped, outputPriority) {
  // Rank: lower = higher priority. For each matched rule compute a numeric
  // priority per output column; sort lexicographically.
  const indexed = matches.map((_, i) => i);
  indexed.sort((ai, bi) => {
    for (let c = 0; c < outputPriority.length; c++) {
      const pri = outputPriority[c];
      if (!pri) continue;
      const va = extractColumn(shaped[ai], c, outputPriority);
      const vb = extractColumn(shaped[bi], c, outputPriority);
      const ra = pri.indexOf(va);
      const rb = pri.indexOf(vb);
      const na = ra < 0 ? Infinity : ra;
      const nb = rb < 0 ? Infinity : rb;
      if (na !== nb) return na - nb;
    }
    return 0;
  });
  return indexed.map((i) => shaped[i]);
}

function extractColumn(shape, col, outputPriority) {
  if (outputPriority.length === 1) return shape;
  // object shape
  const keys = Object.keys(shape);
  return shape[keys[col]];
}

function collect(shaped, aggregation, matches) {
  if (!aggregation) return shaped;
  const allScalar = shaped.every((s) => typeof s !== 'object' || s === null);
  if (allScalar) return aggregateScalars(shaped, aggregation);
  // multi-output aggregate: apply per column
  const keys = Object.keys(shaped[0]);
  const out = {};
  keys.forEach((k) => {
    const col = shaped.map((s) => s[k]);
    out[k] = aggregateScalars(col, aggregation);
  });
  return out;
}

function aggregateScalars(values, aggregation) {
  const nums = values.filter((v) => typeof v === 'number');
  switch (aggregation) {
    case 'SUM':   return nums.reduce((a, b) => a + b, 0);
    case 'COUNT': return values.length;
    case 'MIN':   return nums.length ? Math.min(...nums) : null;
    case 'MAX':   return nums.length ? Math.max(...nums) : null;
    default:      return values;
  }
}
